import moment from "moment"
import {
  chartDateFormat,
  COMPRESSOR_COUNT,
  COMPRESSOR_TYPE,
  CONDENSOR_TYPE,
  CIRCUIT_COUNT,
  CHARACTERISTIC_CURVE,
  COMPRESSOR_COUNT_ARRAY,
  COMPRESSOR_MOTOR_STARTER_TYPE,
  BEARING_TYPE,
  ITEM_SEPERATION,
  allowHpathEquipTypes,
  allowRDRApplicabilityCheckEquipTypes
} from "../../../constant"
import {
  LifeCycle_CHART_CATEGORY,
  LineWithControlRangeAndMean_CHART_CATEGORY,
  StackedBar_CHART_CATEGORY
} from "src/blitz/Charts/constants"
import {
  has,
  isArray,
  isEmpty,
  isFunction,
  isObject,
  isString,
  uniq
} from "lodash"
import { hPathIdsAndStateValidator } from "../../common"
import { getCircuitAndCompressorInstances as getCircuitAndCompressorInstancesA } from "../../applicability"
import { getCircuitAndCompressorInstances } from "../../Exceptions/helper"

export const generateChartPayloadBody = (
  chartConfig: any = {},
  equipmentSelected,
  buildingId,
  sDate,
  eDate,
  tz = "",
  added = false,
  addedAxisList: any = [],
  applicableProperties: any = [],
  selectedEquipmentProperties: any = [],
  systemOfMeasure: any = "",
  only15Min: any = false
) => {
  const startDate = moment(sDate, chartDateFormat)
    .locale("en")
    .format("YYYY-MM-DD")
  const endDate = moment(eDate, chartDateFormat)
    .locale("en")
    .format("YYYY-MM-DD")
  const buildingDateTime = moment.tz.zone(tz)
    ? moment.tz(tz)?.locale("en").format("YYYY-MM-DD HH:mm:ss")
    : moment().locale("en").format("YYYY-MM-DD HH:mm:ss")
  const chartType =
    chartConfig?.type === StackedBar_CHART_CATEGORY
      ? "Bar"
      : chartConfig?.type === LineWithControlRangeAndMean_CHART_CATEGORY
      ? "TimeLine"
      : chartConfig?.type
  const hasTimeline =
    chartConfig?.axisConfig?.some(
      (axis) => axis?.axisName?.toLowerCase() === "timeline"
    ) ||
    addedAxisList?.some((axis) => axis?.axisName?.toLowerCase() === "timeline")

  let payload: any = {
    category: chartConfig?.category,
    chartType: chartType,
    equipmentType: chartConfig?.equipmentType,
    buildingDateTime,
    building: {
      id: buildingId,
      pId: [],
      type: chartConfig?.equipmentType
    },
    weather: [],
    settings: [],
    systemOfMeasure: systemOfMeasure ?? "IP",
    only15Min: hasTimeline ? false : only15Min
  }

  if (chartConfig?.type !== LifeCycle_CHART_CATEGORY) {
    payload.startDate = startDate
    payload.endDate = endDate
  }
  if (!added) {
    const payloadObject = structureFromConfig(
      chartConfig,
      equipmentSelected,
      buildingId,
      applicableProperties,
      selectedEquipmentProperties
    )

    const equipmentTypeObject: any = {}
    const configEquipmentsPropertiesObjects = payloadObject?.equipment || []

    addedAxisList?.forEach((obj) => {
      const added = obj?.added || {}
      Object.keys(added)?.forEach((equipType) => {
        if (!equipmentTypeObject?.[equipType]) {
          equipmentTypeObject[equipType] = []
        }
        let addedLegends = added?.[equipType] || []
        addedLegends = addedLegends?.map((item) => {
          const sep = item?.split(ITEM_SEPERATION)
          return [sep?.[0], sep?.[1]]
        })
        equipmentTypeObject[equipType].push(...addedLegends)
      })
    })

    configEquipmentsPropertiesObjects?.forEach((obj) => {
      if (!equipmentTypeObject?.[obj?.type]) {
        equipmentTypeObject[obj?.type] = []
      }
      const equipType = obj?.type || ""
      const pIDs = obj?.pId?.map((i) => i) || []
      const eIDs = obj?.eId?.map((i) => i?.id) || []
      const combinationArray = []

      pIDs?.forEach((pid) => {
        eIDs?.forEach((eid) => {
          combinationArray.push([eid, pid])
        })
      })
      equipmentTypeObject[equipType].push(...combinationArray)
    })

    const equipments = []
    Object.keys(equipmentTypeObject)?.forEach((equipmentType) => {
      const equipmentPropertyObjects =
        equipmentTypeObject?.[equipmentType] || []

      let consolidatedData = consolidateData(equipmentPropertyObjects)
      consolidatedData = consolidatedData?.map((obj) => {
        return {
          ...obj,
          type: equipmentType
        }
      })
      equipments.push(...consolidatedData)
    })

    payload = {
      ...payload,
      lifecycle: payloadObject?.lifecycle,
      systemOfMeasure: systemOfMeasure,
      building: payloadObject?.building,
      equipment: equipments, // new equipment array with uom is included
      weather: payloadObject?.weather,
      settings: payloadObject?.settings,
      characteristicCurve: payloadObject?.characteristicCurve,
      analyticResults: payloadObject?.analyticResults
    }

    if (chartConfig?.category?.toLowerCase() === "equipment") {
      payload.exceptions = payloadObject?.exceptions
    }
  } else {
    const newAdded = added ? added : {}
    payload = {
      ...payload,
      ...newAdded
    }
  }
  return payload
}

export const structureFromConfig = (
  chartConfig: any = {},
  equipmentSelected: any = {},
  buildingId,
  applicableProperties: any = [],
  selectedEquipmentProperties: any = []
) => {
  const selectedEquipmentCustomHPathProperties =
    extractChartCustomHpathProperties(
      equipmentSelected?.value?.compCondType,
      equipmentSelected?.value?.instance
    )

  // Pre-calculate normalized chiller characteristics once for condition evaluations across axes and children
  const chillerCharacteristics = getChillerCharacteristics(
    selectedEquipmentCustomHPathProperties,
    equipmentSelected?.value?.instance
  )

  const { circuitInstances, compressorInstances, instanceObject } =
    getCircuitAndCompressorInstancesA(selectedEquipmentProperties)

  const compCountAvailable = compressorInstances?.length
  const cktCountAvailable = circuitInstances?.length
  const axisConfigList = chartConfig?.axisConfig || []
  const structuredObject: any = {
    building: {
      id: buildingId,
      pId: [],
      type: chartConfig?.equipmentType
    },
    equipment: [],
    weather: [],
    settings: [],
    axes: [],
    characteristicCurve: {
      parameters: [],
      instances: [],
      id: equipmentSelected?.value?.id
    }
  }

  if (chartConfig?.title === LifeCycle_CHART_CATEGORY) {
    const lifeCycleAxis = axisConfigList?.find(
      (o) => o?.axisName === "lifecycle"
    )

    structuredObject.equipment = [
      {
        eId: [{ id: equipmentSelected?.value?.id }],
        type: equipmentSelected?.value?.type,
        pId: []
      }
    ]

    structuredObject.lifecycle = {
      eId: equipmentSelected?.value?.id,
      type: equipmentSelected?.value?.type,
      testNames: {
        ...(lifeCycleAxis?.lifecycle?.testNames || {})
      },
      characteristics: {
        ...instanceObject
      }
    }
  }
  if (equipmentSelected?.value?.id) {
    structuredObject.analyticResults = {
      eId: equipmentSelected?.value?.id,
      atName: {}
    }
  }

  if (chartConfig?.category?.toLowerCase() === "equipment") {
    structuredObject.exceptions = {
      eId: equipmentSelected?.value?.id || "",
      exName: []
    }
  }

  const allChild = {}
  const allSibling = {}
  const allSiblingCustomProps = {}
  const parentObject = {
    type: chartConfig?.equipmentType,
    eId: [{ id: equipmentSelected?.value?.id }],
    pId: []
  }

  axisConfigList.forEach((obj) => {
    const axisObject = {
      axisName: obj?.axisName,
      axisType: obj?.axisType,
      displayName: obj?.displayName,
      symbol: obj?.symbol,
      UoM: obj?.UoM
    }
    structuredObject.axes.push(axisObject)

    if (obj?.axisName === CHARACTERISTIC_CURVE && obj?.curveList) {
      const circuitCount =
        selectedEquipmentCustomHPathProperties?.[CIRCUIT_COUNT]
      const instanceArray = []
      if (circuitCount) {
        for (let i = 1; i <= circuitCount; i++) {
          instanceArray.push(`CKT${i}`)
        }
      }
      structuredObject.characteristicCurve.parameters = obj?.curveList
      structuredObject.characteristicCurve.instances = instanceArray
    }

    if (obj?.parameterList && isArray(obj?.parameterList?.parent)) {
      if (chartConfig?.category === "Equipment") {
        parentObject.pId.push(...obj?.parameterList?.parent)
      } else {
        structuredObject.building.pId.push(...obj?.parameterList?.parent)
        structuredObject.building.pId = uniq(structuredObject?.building?.pId)
      }
    }

    if (isArray(obj?.weatherList)) {
      structuredObject.weather.push(...obj?.weatherList)
    } else if (isObject(obj?.weatherList)) {
      // CMSAAWS-2434
      // weather list is coming in two formats in different API responses, handling both the cases here and creating a unified weatherList array to check the presence of propertyKey
      // weather list prop also has to support hPath - condition addition
      Object.keys(obj?.weatherList)?.map((key) => {
        const conditionKey = extractConditionKey(key)
        const hpathValidator = hPathIdsAndStateValidator?.[conditionKey]?.test
        const chillerType = selectedEquipmentProperties?.find(
          (x) => x?.targetKey === "CondenserType"
        )?.value
        const conditionCheck = hpathValidator({
          chillerType: chillerType?.replace(/\s+/g, "")?.trim()?.toLowerCase()
        }) // Pass any required parameters for the condition check
        if (conditionCheck) {
          structuredObject.weather.push(...obj?.weatherList[key])
        }
      })
    }
    if (isArray(obj?.settingList)) {
      structuredObject.settings.push(...obj?.settingList)
    }

    if (obj?.parameterList && !isEmpty(obj?.parameterList?.child)) {
      const child = obj?.parameterList?.child || {}
      if (allowHpathEquipTypes(parentObject?.type)) {
        Object.keys(child)?.map((key) => {
          if (key?.includes("CustomHpath")) {
            let compatibleTargetKeys = []

            const conditionKey = extractConditionKey(key)
            const conditionControlled =
              hPathIdsAndStateValidator?.[conditionKey]
            if (
              conditionControlled &&
              parentObject?.type?.toLowerCase() === "ahu"
            ) {
              const conditionalCharac = conditionControlled?.conditionalCharac
              const test = conditionControlled?.test
              const characetristics: any = {}
              if (conditionalCharac?.size) {
                selectedEquipmentProperties?.forEach((o) => {
                  if (conditionalCharac?.has(o?.targetKey)) {
                    characetristics[o?.targetKey] = o?.value
                  }
                })
                const conditionPass = isFunction(test)
                  ? test(characetristics)
                  : false
                if (conditionPass) {
                  compatibleTargetKeys = child?.[key] || []
                }
              }
            } else if (
              conditionControlled &&
              conditionControlled?.test &&
              parentObject?.type?.toLowerCase() === "chiller"
            ) {
              const test = conditionControlled?.test
              // Evaluate validator condition using pre-computed chiller characteristics
              const conditionPass = isFunction(test)
                ? test(chillerCharacteristics)
                : false

              if (conditionPass) {
                compatibleTargetKeys = child?.[key] || []
              }
            } else {
              const requiredTargetKeys = child[key]
              if (allowRDRApplicabilityCheckEquipTypes(parentObject?.type)) {
                const availableTargetKeys = applicableProperties?.map(
                  (item) => item?.targetKey
                )
                compatibleTargetKeys = requiredTargetKeys?.filter((targetKey) =>
                  availableTargetKeys?.some((availableTargetKey) =>
                    availableTargetKey?.includes(targetKey)
                  )
                )
              } else {
                compatibleTargetKeys = requiredTargetKeys
              }
            }

            if (key.includes("child")) {
              const childPIds = circuitCompressorLogic(
                key,
                selectedEquipmentCustomHPathProperties?.[CIRCUIT_COUNT],
                selectedEquipmentCustomHPathProperties?.[
                  COMPRESSOR_COUNT_ARRAY
                ],
                compatibleTargetKeys
              )
              parentObject.pId.push(
                ...FilteredPIDInInstanceCharts(childPIds, chartConfig)
              )
            } else if (key.includes("weather")) {
              structuredObject.weather.push(...compatibleTargetKeys)
            } else {
              parentObject.pId.push(...compatibleTargetKeys)
            }
          } else {
            const childPIds = circuitCompressorLogic(
              key,
              selectedEquipmentCustomHPathProperties?.[CIRCUIT_COUNT],
              selectedEquipmentCustomHPathProperties?.[COMPRESSOR_COUNT_ARRAY],
              child[key]
            )
            parentObject.pId.push(
              ...FilteredPIDInInstanceCharts(childPIds, chartConfig)
            )
          }
        })
      } else {
        Object.keys(child)?.map((key) => {
          if (!has(allChild, key)) {
            allChild[key] = []
          }
          allChild[key].push(...child[key])
        })
      }
    }
    if (obj.parameterList && !isEmpty(obj.parameterList.sibling)) {
      const sibling = obj.parameterList.sibling || {}

      Object.keys(sibling)?.map((key) => {
        if (isArray(sibling?.[key])) {
          if (!has(allSibling, key)) {
            allSibling[key] = []
          }
          allSibling[key].push(...sibling[key])
        } else {
          if (allowHpathEquipTypes(key)) {
            const silblingCustomEquip = equipmentSelected?.siblingList?.filter(
              (obj) => obj?.type === key
            )
            silblingCustomEquip?.forEach((equip) => {
              const siblingCustomEquipProperties =
                extractChartCustomHpathProperties(
                  equip?.compCondType,
                  equip?.instance
                )
              const siblingObj = sibling[key]
              let siblingPIDs = []
              Object?.keys(sibling[key])?.forEach((customKey) => {
                siblingPIDs = circuitCompressorLogic(
                  customKey,
                  siblingCustomEquipProperties[CIRCUIT_COUNT],
                  siblingCustomEquipProperties[COMPRESSOR_COUNT_ARRAY],
                  siblingObj[customKey]
                )
              })
              allSiblingCustomProps[`${equip?.id}_&&_${equip?.type}`] =
                FilteredPIDInInstanceCharts(siblingPIDs, chartConfig)
            })
          } else {
            if (!has(allSibling, key)) {
              allSibling[key] = []
            }
            allSibling[key].push(...sibling[key])
          }
        }
      })
    }

    if (
      chartConfig?.category?.toLowerCase() === "equipment" &&
      obj?.axisName?.toLowerCase() === "exceptions"
    ) {
      if (Array.isArray(obj?.exceptionList) && obj?.exceptionList?.length > 0) {
        const childExceptions =
          obj?.exceptionList &&
          obj?.exceptionList.filter((list) => !!list.child)
        if (childExceptions.length > 0) {
          const formattedChildExceptions = childExceptions.reduce(
            (acc, curr) => {
              if (acc[curr.child]) {
                acc[curr.child].push(curr.name)
              } else {
                acc[curr.child] = [curr.name]
              }
              return acc
            },
            {}
          )
          structuredObject.exceptions = equipmentSelected.childList
            .filter((child) =>
              Object.keys(formattedChildExceptions).includes(child.type)
            )
            .map((item) => ({
              eId: item.id,
              exName: FilteredPIDInInstanceCharts(
                formattedChildExceptions[item.type],
                chartConfig
              )
            }))
        } else {
          const { circuitInstances, compressorInstances } =
            getCircuitAndCompressorInstances(selectedEquipmentProperties)
          obj?.exceptionList?.forEach((exceptionObj: any) => {
            const exception = exceptionObj?.name
            if (isString(exception)) {
              const modifierRegex = new RegExp(/[X$#@!%^*&*[\]]{2}/)
              const circuitRegex = new RegExp(
                /[a-zA-z]*([Cc][kK][tT])[a-zA-z]*/
              )
              const compressorRegex = new RegExp(
                /[a-zA-z]*([Cc][Oo][mM][pP])[a-zA-z]*/
              )
              const compressorKeywordRegex =
                /([Cc][Oo][mM][pP][rR][eE][sS][sS][oO][rR])/
              if (
                circuitRegex?.test(exception) &&
                modifierRegex?.test(exception)
              ) {
                circuitInstances?.forEach((instance) => {
                  if (
                    !structuredObject?.exceptions?.exName?.includes(exception)
                  ) {
                    structuredObject.exceptions.exName.push(
                      exception?.replace(modifierRegex, instance?.trim())
                    )
                  }
                })
              } else if (
                (compressorRegex?.test(exception) ||
                  compressorKeywordRegex?.test(exception)) &&
                modifierRegex?.test(exception)
              ) {
                compressorInstances?.forEach((instance) => {
                  if (
                    !structuredObject?.exceptions?.exName?.includes(exception)
                  ) {
                    structuredObject.exceptions.exName.push(
                      exception?.replace(modifierRegex, instance?.trim())
                    )
                  }
                })
              } else {
                if (
                  !structuredObject?.exceptions?.exName?.includes(exception)
                ) {
                  structuredObject.exceptions.exName.push(exception)
                }
              }
            }
          })
          structuredObject.exceptions.exName = FilteredPIDInInstanceCharts(
            structuredObject.exceptions.exName,
            chartConfig
          )
        }
      }
    }

    if (
      !isEmpty(obj?.analyticsResultsList) &&
      (compCountAvailable || cktCountAvailable)
    ) {
      Object.keys(obj?.analyticsResultsList)?.forEach((analyticKey) => {
        if (analyticKey?.toLowerCase()?.includes("comp")) {
          if (compCountAvailable) {
            const cktCompInstances = compressorInstances?.filter(
              (cI = "") =>
                isString(cI) && cI?.includes(`${chartConfig?.charac?.circuit}`)
            )

            const isSingleInstance =
              compressorInstances?.length === 1 &&
              circuitInstances?.length === 1

            cktCompInstances?.forEach((compInstance: any) => {
              const newKey = analyticKey?.replace(
                isSingleInstance ? "#$ " : "#$",
                isSingleInstance ? "" : compInstance
              )
              structuredObject.analyticResults.atName[newKey] = {}
              const childAnalytics = obj?.analyticsResultsList?.[analyticKey]
              Object.keys(childAnalytics)?.forEach((childAnalyticKey) => {
                const childAnalytic = {
                  ...(childAnalytics?.[childAnalyticKey] || {})
                }
                if ("displayName" in childAnalytic) {
                  delete childAnalytic.displayName
                }
                structuredObject.analyticResults.atName[newKey][
                  childAnalyticKey
                ] = childAnalytic
              })
            })
          }
        }
        // Revisit:2025-05-29 - Info not yet available
        // if (analyticKey?.toLowerCase()?.includes("ckt")) {
        //   if (cktCountAvailable) {
        //     circuitInstances?.forEach((cktInstance: any) => {

        //     })
        //   }
        // }
      })
    }
  })

  parentObject.pId = uniq(parentObject.pId)

  if (parentObject?.pId?.length > 0) {
    structuredObject.equipment.push(parentObject)
  }

  Object.keys(allChild)?.map((key) => {
    const eIds =
      equipmentSelected?.childList
        ?.filter((obj) => obj?.type === key)
        ?.map((obj) => {
          return {
            id: obj?.id
          }
        }) || []
    const uniqPIds = uniq(allChild[key]) || []
    const equipmentObject: any = {
      type: key,
      eId: eIds,
      pId: uniqPIds
    }

    if (equipmentObject?.pId?.length > 0 && equipmentObject?.eId?.length > 0) {
      structuredObject.equipment.push(equipmentObject)
    }
  })

  Object.keys(allSibling)?.map((key) => {
    const eIds =
      equipmentSelected?.siblingList
        ?.filter((obj) => obj?.type === key)
        ?.map((obj) => {
          return {
            id: obj?.id
          }
        }) || []
    const uniqPIds = uniq(allSibling?.[key]) || []
    const equipmentObject: any = {
      type: key,
      eId: eIds,
      pId: uniqPIds
    }

    if (equipmentObject?.pId?.length > 0 && equipmentObject?.eId?.length > 0) {
      structuredObject.equipment.push(equipmentObject)
    }
  })
  Object.keys(allSiblingCustomProps)?.map((key) => {
    const uniqPIds = uniq(allSiblingCustomProps?.[key]) || []
    const [id, type] = key?.split("_&&_")
    const equipmentObject: any = {
      type: type,
      eId: [{ id: id }],
      pId: uniqPIds
    }

    if (equipmentObject?.pId?.length > 0 && equipmentObject?.eId?.length > 0) {
      structuredObject.equipment.push(equipmentObject)
    }
  })

  return structuredObject
}

// CustomHPath: Logic for dynamic property formation
export const circuitCompressorLogic = (
  property,
  circuitCount,
  compressorCountArray,
  childArray
) => {
  const childPIds = []
  if (property.includes("Circuit") && circuitCount && childArray?.length > 0) {
    for (const item of childArray) {
      const propertyName = item.endsWith("1") ? item.slice(0, -1) : item
      for (let i = 1; i <= circuitCount; i++) {
        childPIds.push(`${propertyName}${i}`)
      }
    }
  } else if (
    property.includes("Compressor") &&
    circuitCount &&
    compressorCountArray?.length > 0 &&
    childArray?.length > 0
  ) {
    for (const item of childArray) {
      const propertyName = item.endsWith("1A") ? item.slice(0, -2) : item
      for (const index in compressorCountArray) {
        for (let j = 1; j <= compressorCountArray[index]; j++) {
          childPIds.push(
            `${propertyName}${parseInt(index) + 1}${String.fromCharCode(
              64 + j
            )}`
          )
        }
      }
    }
  } else if (property.includes("generic")) {
    childPIds.push(...childArray)
  }
  return childPIds
}

export const parseInstanceString = (instance) => {
  try {
    if (Array.isArray(instance)) {
      return instance
    } else {
      const instanceArray = instance
        .replace(/\[|\]|\"|\'|\\|\{|\}| /g, "")
        .split(",")
      return instanceArray
    }
  } catch (error) {
    return []
  }
}

export const parseCompCondTypeString = (compCondType) => {
  try {
    const regex = /"([^"\\]*(?:\\.[^"\\]*)*)"|\[([^\]]+)\]|([^[\s"\\]+)/g
    const matches = Array.from(compCondType.matchAll(regex)) || []
    const values = matches?.map((match) => {
      const value = (
        match?.[1] !== undefined
          ? match?.[1]
          : match?.[2] !== undefined
          ? match?.[2]
          : match?.[3]
      )?.trim()
      return value?.replace(/^"(.*)"$/, "$1")
    })
    return values
  } catch (error) {
    return []
  }
}

export const extractCountValuesFromInstance = (instance = "") => {
  let circuitCount = 0
  const compressorCountArray = []
  const instanceArray = parseInstanceString(instance)
  instanceArray?.forEach((inst) => {
    if (inst?.toLowerCase()?.includes("ckt")) {
      circuitCount++
    }
  })
  if (instanceArray?.includes("Comp")) {
    for (let i = 0; i < circuitCount; i++) {
      compressorCountArray?.push(1)
    }
  } else {
    for (let i = 1; i <= circuitCount; i++) {
      let compressorCount = 0
      instanceArray?.forEach((inst) => {
        if (inst?.toLowerCase()?.startsWith(i?.toString())) {
          compressorCount++
        }
      })
      compressorCountArray.push(compressorCount)
    }
  }
  return {
    circuitCount,
    compressorCountArray
  }
}

export const extractChartCustomHpathProperties = (
  compCondType = "",
  instance = ""
) => {
  const compCondTypeArray = parseCompCondTypeString(compCondType)
  const condensorType =
    compCondTypeArray?.length >= 1 ? compCondTypeArray[0]?.split("#")[0] : null
  const compressorType =
    compCondTypeArray?.length >= 1 ? compCondTypeArray[0]?.split("#")[1] : null
  const compressorMotorStarterType =
    compCondTypeArray?.length >= 1 ? compCondTypeArray[0]?.split("#")[2] : null
  const bearingType =
    compCondTypeArray?.length >= 1 ? compCondTypeArray[0]?.split("#")[3] : null
  const { circuitCount, compressorCountArray } =
    extractCountValuesFromInstance(instance)

  const averageCompressorCount =
    compressorCountArray?.reduce((a, b) => parseInt(a) + parseInt(b), 0) /
    circuitCount

  return {
    [CONDENSOR_TYPE]: condensorType,
    [COMPRESSOR_TYPE]: compressorType,
    [COMPRESSOR_MOTOR_STARTER_TYPE]: compressorMotorStarterType,
    [BEARING_TYPE]: bearingType,
    [CIRCUIT_COUNT]: circuitCount,
    [COMPRESSOR_COUNT]: averageCompressorCount,
    [COMPRESSOR_COUNT_ARRAY]: compressorCountArray
  }
}

// Normalizes and builds chiller equipment characteristics for hPath condition validation
export const getChillerCharacteristics = (
  customProps: Record<string, any> = {},
  instance?: string
) => {
  const normalize = (key: string) =>
    (customProps?.[key] || "").replace(/\s+/g, "").toLowerCase()

  const circuitCount = customProps?.[CIRCUIT_COUNT]
  const compressorCountArray = customProps?.[COMPRESSOR_COUNT_ARRAY] || []
  const totalCompressors = compressorCountArray.reduce(
    (sum: number, count: any) => sum + (Number(count) || 0),
    0
  )

  return {
    chillerType: normalize(CONDENSOR_TYPE),
    compressorType: normalize(COMPRESSOR_TYPE),
    compressorMotorStarterType: normalize(COMPRESSOR_MOTOR_STARTER_TYPE),
    bearingType: normalize(BEARING_TYPE),
    cricuitType:
      circuitCount > 1 ? "multiple" : circuitCount === 1 ? "single" : undefined,
    compressorCountType:
      totalCompressors > 1 ? "multiple" : totalCompressors === 1 ? "single" : undefined,
    instance
  }
}

// Function to extract condition code ex: h1020
export const extractConditionKey = (str = "") => {
  let startIndex = -1
  let key = ""

  for (let i = 0; i < str.length; i++) {
    if (str[i] === "-" && startIndex === -1) {
      startIndex = i + 1
    } else if (str[i] === "$" && startIndex !== -1) {
      return key
    } else if (startIndex !== -1) {
      key += str[i]
    }
  }
  return null
}
// CustomHPath: Logic for dynamic property formation

const mergeUnique = (arr) => {
  const newSet: any = new Set(arr)
  return [...newSet]
}

// Function to consolidate the data to minise the combinations repeatability
const consolidateData = (data) => {
  const eidToPidMap = new Map()

  data.forEach(([eId, pId]) => {
    if (!eidToPidMap.has(eId)) {
      eidToPidMap.set(eId, new Set())
    }
    eidToPidMap.get(eId).add(pId)
  })

  const combined = {}
  eidToPidMap.forEach((pids, id) => {
    const key = Array.from(pids).sort().join(",")
    if (!combined[key]) {
      combined[key] = new Set()
    }
    combined[key].add(id)
  })

  const result = Object.entries(combined).map(([pids, ids]) => {
    const cds: any = ids
    return {
      eId: Array.from(cds).map((id) => ({ id })),
      pId: mergeUnique(pids.split(","))
    }
  })

  return result
}

//filter the properties incase of Instance circuit charts
const FilteredPIDInInstanceCharts = (
  PidArray: Array<string>,
  chartConfig: any
) => {
  if (
    chartConfig?.instance?.toLowerCase() === "circuit" ||
    chartConfig?.instance?.toLowerCase() === "compressor"
  ) {
    const chartInstance = circuitCountExtracorFromString(chartConfig?.title)
    if (!chartInstance) {
      return PidArray
    }
    return PidArray.filter((item) => {
      // Handle vibration properties with instance suffix (e.g., "CompressorVibration15Mins2ZMaxVelocity1A")
      // These properties end with circuit+compressor instance like "1A", "2B", etc.
      const vibrationInstanceMatch = item?.match(/(\d+[A-Z])$/i)
      if (vibrationInstanceMatch) {
        const instanceSuffix = vibrationInstanceMatch[1]
        const circuitNum = parseInt(instanceSuffix.slice(0, -1), 10)
        // Include if the circuit number matches the chart instance
        return circuitNum === chartInstance
      }

      // Default behavior for non-vibration properties
      const itemInstance = circuitCountExtracorFromString(item)
      return itemInstance === chartInstance || itemInstance === null
    })
  }
  return PidArray
}

const circuitCountExtracorFromString = (inputStr) => {
  const match = inputStr.match(/(ckt|comp|compressor|circuit)[^\d]*(\d+)/i)
  if (match) {
    return parseInt(match[2], 10)
  } else {
    return null
  }
}

